import { AnimatePresence, m } from 'framer-motion';
import { Fragment, useState } from 'react';
import { Checkbox } from '@ui/forms/toggle/checkbox';
import { opacityAnimation } from '@ui/animation/opacity-animation';
import { Skeleton } from '@ui/skeleton/skeleton';
import { useProducts } from '@common/billing/pricing-table/use-products';
import { Product } from '@common/billing/product';
import {
  findBestPrice,
  UpsellBillingCycle,
} from '@common/billing/pricing-table/find-best-price';
import { useAuth } from '@common/auth/use-auth';
import clsx from 'clsx';
import { Chip } from '@ui/forms/input-field/chip-field/chip';
import { Trans } from '@ui/i18n/trans';
import { FormattedPrice } from '@common/billing/formatted-price';
import { FormattedCurrency } from '@ui/i18n/formatted-currency';
import { Button } from '@ui/buttons/button';
import { Link } from 'react-router-dom';
import { setInLocalStorage } from '@ui/utils/hooks/local-storage';
import { trackFacebookEvent, getFbc, getFbp } from '../../facebook/facebook-track';
import { ProductFeatureList } from '@common/billing/pricing-table/product-feature-list';
import { useBillingUser } from '../billing-page/use-billing-user';
import { useCurrency } from '@common/billing/currency';
import { openDialog } from '@ui/overlays/store/dialog-store';
import { ConfirmationDialog } from '@ui/overlays/dialog/confirmation-dialog';

interface PricingTableProps {
  selectedCycle: UpsellBillingCycle;
  className?: string;
  productLoader?: string;
}
export function PricingTable({
  selectedCycle,
  className,
  productLoader,
}: PricingTableProps) {
  const query = useProducts(productLoader);
  const { subscription }: any = useBillingUser();
  const [showAll, setShowAll] = useState(false);
  return (
    <div className="flex flex-col w-full">
      {subscription && (
        <div className="flex justify-center mb-20">
          <Checkbox checked={showAll} onChange={(e) => setShowAll(e.target.checked)}>
            <Trans message="Show all plans" />
          </Checkbox>
        </div>
      )}
      <div
        className={clsx(
          'flex flex-row items-stretch gap-24 overflow-x-auto pb-16 md:flex-row md:justify-start',
          className,
        )}
        style={{ scrollbarColor: 'auto' }}
      >
        <AnimatePresence initial={false} mode="wait">
          {query.data ? (
            <PlanList
              key="plan-list"
              plans={query.data.products}
              selectedPeriod={selectedCycle}
              subscription={subscription}
              showAll={showAll}
            />
          ) : (
            <SkeletonLoader key="skeleton-loader" />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

interface PlanListProps {
  plans: Product[];
  selectedPeriod: UpsellBillingCycle;
  subscription: any;
  showAll?: boolean;
}
function PlanList({ plans, selectedPeriod, subscription, showAll }: PlanListProps) {
  const { isLoggedIn, isSubscribed, user } = useAuth();
  const { currency } = useCurrency();

  const filteredPlans = plans.filter((plan) => {
    // Find if this plan has a price matching the selected period and currency
    const hasPriceForCurrency = plan.prices.some(
      p =>
        p.interval === (selectedPeriod === 'month' ? 'month' : 'year') &&
        p.currency === currency
    );
    return !plan.hidden && hasPriceForCurrency;
  });
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return (
    <Fragment>
      {filteredPlans
        .filter((plan: any) => {
          if (showAll) {
            return true;
          }
          if (subscription && subscription.product && subscription.price) {
            // Current plan is always visible
            if (plan.id === subscription.product_id) {
              return true;
            }

            const expiryDate = subscription.ends_at
              ? new Date(subscription.ends_at)
              : (subscription.renews_at ? new Date(subscription.renews_at) : null);

            if (expiryDate) {
              const endDate = new Date(expiryDate);
              const currentDate = new Date();
              endDate.setHours(0, 0, 0, 0);
              currentDate.setHours(0, 0, 0, 0);

              const diff = endDate.getTime() - currentDate.getTime();
              const daysLeft = Math.ceil(diff / (1000 * 60 * 60 * 24));
              const remainingDays = daysLeft > 0 ? daysLeft : 0;

              // Within 3 days of expiry: can see all plans
              if (remainingDays <= 3) {
                return true;
              }

              const interval = subscription.price.interval || 'month';
              const intervalCount = subscription.price.interval_count || 1;
              const cycleStart = new Date(expiryDate);
              if (interval === 'year') {
                cycleStart.setFullYear(cycleStart.getFullYear() - intervalCount);
              } else if (interval === 'week') {
                cycleStart.setDate(cycleStart.getDate() - (7 * intervalCount));
              } else if (interval === 'day') {
                cycleStart.setDate(cycleStart.getDate() - intervalCount);
              } else { // month
                cycleStart.setMonth(cycleStart.getMonth() - intervalCount);
              }
              let totalDays = Math.ceil((expiryDate.getTime() - cycleStart.getTime()) / (1000 * 60 * 60 * 24));
              totalDays = totalDays > 0 ? totalDays : 29;

              const remainingFraction = Math.min(1.0, Math.max(0.0, remainingDays / totalDays));
              const currentPlanCredit = subscription.price.amount * remainingFraction;

              // Check if plan has a price in the selected cycle and currency that is greater than remaining credit
              const planPrice = findBestPrice(selectedPeriod, plan.prices, currency);
              if (planPrice && planPrice.amount > currentPlanCredit) {
                return true;
              }

              return false;
            }
          }
          return true;
        })
        .map((plan: any, index) => {
          const isFirst = index === 0;
          const isLast = index === filteredPlans.length - 1;
          const price = findBestPrice(selectedPeriod, plan.prices, currency);

          let isLowerPlan = false;
          if (subscription && subscription.product && subscription.price) {
            const expiryDate = subscription.ends_at
              ? new Date(subscription.ends_at)
              : (subscription.renews_at ? new Date(subscription.renews_at) : null);

            if (expiryDate) {
              const endDate = new Date(expiryDate);
              const currentDate = new Date();
              endDate.setHours(0, 0, 0, 0);
              currentDate.setHours(0, 0, 0, 0);

              const diff = endDate.getTime() - currentDate.getTime();
              const daysLeft = Math.ceil(diff / (1000 * 60 * 60 * 24));
              const remainingDays = daysLeft > 0 ? daysLeft : 0;

              if (remainingDays > 3) {
                const isCurrentPlan = subscription.product_id === plan.id && subscription.price_id === price?.id;

                if (!isCurrentPlan) {
                  const interval = subscription.price.interval || 'month';
                  const intervalCount = subscription.price.interval_count || 1;
                  const cycleStart = new Date(expiryDate);
                  if (interval === 'year') {
                    cycleStart.setFullYear(cycleStart.getFullYear() - intervalCount);
                  } else if (interval === 'week') {
                    cycleStart.setDate(cycleStart.getDate() - (7 * intervalCount));
                  } else if (interval === 'day') {
                    cycleStart.setDate(cycleStart.getDate() - intervalCount);
                  } else { // month
                    cycleStart.setMonth(cycleStart.getMonth() - intervalCount);
                  }
                  let totalDays = Math.ceil((expiryDate.getTime() - cycleStart.getTime()) / (1000 * 60 * 60 * 24));
                  totalDays = totalDays > 0 ? totalDays : 29;

                  const remainingFraction = Math.min(1.0, Math.max(0.0, remainingDays / totalDays));
                  const currentPlanCredit = subscription.price.amount * remainingFraction;

                  if (!price || price.amount <= currentPlanCredit) {
                    isLowerPlan = true;
                  }
                }
              }
            }
          }

          let upgradeRoute: any;
          if (!isLowerPlan) {
            if (!isLoggedIn) {
              upgradeRoute = `/register?redirectFrom=pricing`;
            }
            if (isSubscribed) {
              upgradeRoute = `/change-plan/${plan.id}/${price?.id}/confirm`;
            }
            if (isLoggedIn && !plan.free) {
              upgradeRoute = `/checkout/${plan.id}/${price?.id}`;
            }
          }

          const buttonElement = isLowerPlan ? 'button' : (upgradeRoute ? Link : undefined);
          const isButtonDisabled = !isLowerPlan && !upgradeRoute;

          return (
            <m.div
              key={plan.id}
              {...opacityAnimation}
              className={clsx(
                  "relative w-full rounded-3xl border bg-white px-28 py-28 my-20 text-black md:min-w-264 md:max-w-400",
                  "shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl",
                  plan.recommended
                    ? "border-2 border-[#3B82F6] shadow-2xl shadow-[#3B82F6]/20 ring-2"
                    : "border-primary-input",
                  isFirst && "ml-auto",
                  isLast && "mr-auto",
                )}
                
              >
              {plan.recommended && (
                <div className="pointer-events-none absolute inset-0 rounded-3xl from-primary/12 via-transparent to-transparent" />
              )}

              <div className="mb-10">
                <Chip
                  radius="rounded"
                  size="sm"
                  className={clsx(
                    'absolute -top-44 left-1/2 -translate-x-1/2 w-min py-20 px-30 bg-gradient-to-r from-[#0072ff] to-[#00c6ff] text-white shadow-lg rounded-full flex items-center gap-1 text-sm font-semibold ',
                    !plan.recommended && 'invisible',
                  )}
                >
                  <Trans message="Most popular" />
                </Chip>
                <div className="flex items-center justify-between gap-12">
                  <div
                    className="mb-4 text-2xl font-bold"
                    style={{ fontFamily: 'Archivo ,serif' }}
                  >
                    <Trans message={plan.name} />
                  </div>
                  {isLowerPlan && (
                    <div className="text-xs font-semibold text-amber-600 bg-amber-100 px-10 py-4 rounded-full w-fit mb-4">
                      <Trans message="View Only" />
                    </div>
                  )}
                </div>
              </div>
              <div>
                {price ? (
                  <div className="flex flex-col gap-4">
                    <FormattedPrice
                      priceClassName="font-bold text-xl color-primary"
                      periodClassName="text-sm font-semibold"
                      variant="separateLine"
                      price={price}
                    />
                  </div>
                ) : (
                  <div className="text-4xl font-bold">
                    <Trans message="Free" />
                  </div>
                )}
                <div className="mt-50">
                  <Button
                    variant={plan.recommended ? 'flat' : 'outline'}
                    className=" w-full rounded-lg !text-white font-semibold
                        !bg-gradient-to-r !from-[#0072ff] !to-[#00c6ff]  shadow-lg
                        hover:shadow-xl transition border-none"
                    size="md"
                    elementType={buttonElement}
                    disabled={isButtonDisabled}
                    onClick={(e) => {
                      if (isLowerPlan) {
                        e.preventDefault();
                        openDialog(ConfirmationDialog, {
                          title: <Trans message="Downgrade Blocked" />,
                          body: <Trans message="You cannot purchase this plan as it is lower than your current active plan. You can only downgrade after your current plan expires." />,
                          confirm: <Trans message="Okay" />,
                        });
                        return;
                      }
                      if (!isLoggedIn && price && plan) {
                        setInLocalStorage('be.onboarding.selected', {
                          productId: plan.id,
                          priceId: price.id,
                          priceAmount: typeof price.amount === 'string' ? parseFloat(price.amount) : price.amount,
                          currency: price.currency,
                        });
                      }

                      // Track plan click events
                      if (price && plan) {
                        const customData = {
                          content_name: plan.name,
                          content_ids: [plan.id.toString()],
                          value: typeof price.amount === 'string' ? parseFloat(price.amount) : price.amount,
                          currency: price.currency,
                          content_type: 'product',
                        };

                        const savedPhone = typeof window !== 'undefined' ? localStorage.getItem('checkout_phone') || undefined : undefined;
                        const fbcVal = getFbc() || undefined;
                        const fbpVal = getFbp() || undefined;

                        const userData = user ? {
                          em: user.email,
                          external_id: String(user.id),
                          fn: (user.name || '').trim().split(' ')[0] || '',
                          ln: (user.name || '').trim().split(' ').length > 1 ? (user.name || '').trim().split(' ').slice(1).join(' ') : (user.name || '').trim().split(' ')[0] || '',
                          ph: savedPhone,
                          fbc: fbcVal,
                          fbp: fbpVal,
                        } : {
                          ph: savedPhone,
                          fbc: fbcVal,
                          fbp: fbpVal,
                        };

                        trackFacebookEvent('InitiateCheckout', customData, userData);
                        trackFacebookEvent('SubscribedButtonClick', customData, userData);
                        trackFacebookEvent('AddToCart', customData, userData);
                      }
                    }}
                    to={isLowerPlan ? undefined : upgradeRoute}
                  >
                    {plan.free ? (
                      <Trans message="Get started" />
                    ) : (
                      <Trans message="Choose Plan" />
                    )}
                  </Button>
                </div>
                <div className="text-sm text-black mt-20">
                  <Trans message={plan.description} />
                </div>
                <ProductFeatureList product={plan} />
              </div>
            </m.div>
          );
        })}
    </Fragment>
  );
}

function SkeletonLoader() {
  return (
    <Fragment>
      <PlanSkeleton key="skeleton-1" />
      <PlanSkeleton key="skeleton-2" />
      <PlanSkeleton key="skeleton-3" />
    </Fragment>
  );
}

function PlanSkeleton() {
  return (
    <m.div
      {...opacityAnimation}
      className="w-full rounded-lg border px-28 py-90 shadow-lg md:max-w-350"
    >
      <Skeleton className="my-10" />
      <Skeleton className="mb-40" />
      <Skeleton className="mb-40 h-30" />
      <Skeleton className="mb-40 h-40" />
      <Skeleton className="mb-20" />
      <Skeleton />
      <Skeleton />
    </m.div>
  );
}